<script setup lang="ts">
const { item } = defineProps<{
  item: unknown
  lazy: boolean
}>()

const actionDialog = ref(null)

if (item?.Tooltip) {
  actionDialog.value = useUiDialog(`action-and-event-dialog-${item?.id}`)
}
</script>

<template>
  <article
    class="action-and-event"
  >
    <div class="action-and-event__layout">
      <div class="action-and-event__content">
        <h3
          v-if="item?.Title"
          class="action-and-event__heading"
        >
          {{ item.Title }}
        </h3>

        <p
          v-if="item?.Description"
          class="action-and-event__description"
        >
          {{ item.Description }}
        </p>

        <button
          v-if="item?.Tooltip"
          class="action-and-event__tooltip"
          @click="actionDialog.open()"
        >
          <Icon
            name="icon:info"
            class="icon icon--info"
          />
          <span>{{ $t('Read more') }}</span>
        </button>

        <AppLink
          v-if="item?.Button?.Link"
          :to="item?.Button?.Link"
          class="action-and-event__button"
          :class="`action-and-event__button--${item?.Button?.Type}`"
        >
          <span>{{ item?.Button?.Text || $t('Read more') }}</span>
        </AppLink>
      </div>

      <div class="action-and-event__visual">
        <picture
          v-if="item?.Image?.url && item?.Image?.mime?.includes('image')"
          class="action-and-event__picture"
        >
          <img
            class="action-and-event__image"
            :src="useXcdnImage(item?.Image?.url, 'f=rs:fit:400')"
            :alt="item?.Image?.alternativeText || item?.Title || ''"
            :width="item?.Image?.width"
            :height="item?.Image?.height"
            :loading="lazy ? 'lazy' : 'eager'"
            :fetchpriority="lazy ? 'low' : 'high'"
            :decoding="lazy ? 'async' : 'sync'"
          >
        </picture>

        <img
          v-if="item?.ImageBrand?.url"
          class="action-and-event__brand"
          :src="useXcdnImage(item?.ImageBrand?.url, 'f=rs:fit:80')"
          :alt="item?.ImageBrand?.alternativeText || item?.Title || ''"
          :width="item?.ImageBrand?.width"
          :height="item?.ImageBrand?.height"
          :loading="lazy ? 'lazy' : 'eager'"
          :fetchpriority="lazy ? 'low' : 'high'"
          :decoding="lazy ? 'async' : 'sync'"
        >

        <div class="action-and-event__labels">
          <span
            v-if="item.Date"
            class="action-and-event__label"
          >
            <Icon name="icon:calendar" />
            {{ $td(item.Date, { year: 'numeric', month: 'long', day: 'numeric' }) }}
          </span>

          <span
            v-if="item.Location"
            class="action-and-event__label"
          >
            <Icon name="icon:location-empty" />
            {{ item.Location }}
          </span>
        </div>
      </div>
    </div>

    <UiDialog
      v-if="item?.Tooltip"
      :name="`action-and-event-dialog-${item?.id}`"
      dialog-type="modal"
    >
      <template #dialogHeader>
        {{ $t(item.Tooltip?.Title) }}
      </template>

      <StrapiBlocksText
        v-if="item.Tooltip?.Richtext"
        :nodes="item.Tooltip?.Richtext"
      />

      <AppLink
        v-if="item.Tooltip?.Button?.Link"
        :to="item.Tooltip?.Button?.Link"
        class="action-and-event-dialog__button"
        :class="`action-and-event-dialog__button--${item.Tooltip?.Button?.Type}`"
      >
        <span>{{ item.Tooltip?.Button?.Text }}</span>
      </AppLink>
    </UiDialog>
  </article>
</template>

<style>
@import url("~layers/app/components/Strapi/ActionAndEvent.css");
</style>
